/**
 * Google Analytics 4 (GA4) Analytics Helper
 * 
 * Provides helper functions for tracking GA4 events.
 * Works alongside Google Tag Manager (GTM) and uses the same dataLayer.
 * 
 * Usage:
 *   window.ga4Analytics.trackEvent('event_name', { param1: 'value1' });
 *   window.ga4Analytics.trackPageView('/custom-path');
 *   window.ga4Analytics.trackEcommerce('purchase', { transaction_id: '123', value: 99.99 });
 */

(function() {
  'use strict';

  // Check if gtag is available
  function isGA4Available() {
    return typeof window.gtag !== 'undefined';
  }

  /**
   * Track a custom GA4 event
   * @param {string} eventName - The event name (e.g., 'button_click', 'form_submit')
   * @param {Object} eventParams - Event parameters (optional)
   */
  function trackEvent(eventName, eventParams) {
    if (!isGA4Available()) {
      // Fallback to dataLayer for GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...eventParams
      });
      return;
    }

    // Use gtag for direct GA4 tracking
    window.gtag('event', eventName, eventParams || {});
  }

  /**
   * Track a page view
   * @param {string} pagePath - The page path (optional, defaults to current page)
   * @param {string} pageTitle - The page title (optional)
   */
  function trackPageView(pagePath, pageTitle) {
    if (!isGA4Available()) {
      // Fallback to dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'page_view',
        page_path: pagePath || window.location.pathname,
        page_title: pageTitle || document.title
      });
      return;
    }

    const params = {};
    if (pagePath) params.page_path = pagePath;
    if (pageTitle) params.page_title = pageTitle;

    window.gtag('event', 'page_view', params);
  }

  /**
   * Track ecommerce events (add_to_cart, purchase, etc.)
   * @param {string} eventName - Ecommerce event name
   * @param {Object} ecommerceData - Ecommerce data (items, value, currency, etc.)
   */
  function trackEcommerce(eventName, ecommerceData) {
    if (!isGA4Available()) {
      // Fallback to dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ecommerce: ecommerceData
      });
      return;
    }

    // GA4 ecommerce events
    window.gtag('event', eventName, {
      ...ecommerceData
    });
  }

  /**
   * Track add to cart event
   * @param {Object} item - Product item data
   */
  function trackAddToCart(item) {
    trackEcommerce('add_to_cart', {
      currency: item.currency || 'USD',
      value: item.value || item.price || 0,
      items: [{
        item_id: item.id || item.product_id,
        item_name: item.name || item.product_name,
        item_category: item.category || '',
        price: item.price || item.value || 0,
        quantity: item.quantity || 1
      }]
    });
  }

  /**
   * Track purchase event
   * @param {Object} transaction - Transaction data
   */
  function trackPurchase(transaction) {
    trackEcommerce('purchase', {
      transaction_id: transaction.id || transaction.transaction_id,
      value: transaction.value || transaction.total || 0,
      currency: transaction.currency || 'USD',
      items: transaction.items || []
    });
  }

  /**
   * Track view item event
   * @param {Object} item - Product item data
   */
  function trackViewItem(item) {
    trackEcommerce('view_item', {
      currency: item.currency || 'USD',
      value: item.value || item.price || 0,
      items: [{
        item_id: item.id || item.product_id,
        item_name: item.name || item.product_name,
        item_category: item.category || '',
        price: item.price || item.value || 0
      }]
    });
  }

  /**
   * Track search event
   * @param {string} searchTerm - The search query
   */
  function trackSearch(searchTerm) {
    trackEvent('search', {
      search_term: searchTerm
    });
  }

  /**
   * Track custom button click
   * @param {string} buttonName - Button identifier/name
   * @param {string} buttonLocation - Where the button is located (optional)
   */
  function trackButtonClick(buttonName, buttonLocation) {
    trackEvent('button_click', {
      button_name: buttonName,
      button_location: buttonLocation || window.location.pathname
    });
  }

  /**
   * Track form submission
   * @param {string} formName - Form identifier/name
   * @param {boolean} success - Whether submission was successful
   */
  function trackFormSubmit(formName, success) {
    trackEvent('form_submit', {
      form_name: formName,
      form_success: success
    });
  }

  // Public API
  window.ga4Analytics = {
    trackEvent: trackEvent,
    trackPageView: trackPageView,
    trackEcommerce: trackEcommerce,
    trackAddToCart: trackAddToCart,
    trackPurchase: trackPurchase,
    trackViewItem: trackViewItem,
    trackSearch: trackSearch,
    trackButtonClick: trackButtonClick,
    trackFormSubmit: trackFormSubmit,
    isAvailable: isGA4Available
  };

  // Auto-track page views if enabled (will be set by theme.liquid)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // Page view tracking is handled by theme.liquid after GA4 initialization
    });
  }
})();




